import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    q: 'How quickly will I start seeing results?',
    a: 'Most clients see a noticeable uplift within the first 2 weeks. The system responds instantly to every enquiry 24/7, so leads that used to go cold (late-night messages, weekend enquiries, missed calls) get converted before you even start your day. We have helped businesses across the UK automate over 5,200 bookings with a 99% client satisfaction rate.',
  },
  {
    q: 'Does this work for my type of business?',
    a: 'Yes. We specifically built our systems for appointment-based businesses: hair salons, barbers, dental and medical clinics, builders and trades, fitness studios, physiotherapists, cleaners, and other home service providers. If your business runs on bookings and appointments, we can automate it.',
  },
  {
    q: 'What happens when someone enquires outside business hours?',
    a: 'Your AI never clocks off. It responds to every enquiry in under 60 seconds, 24/7, including weekends and bank holidays. It answers questions about your services and pricing, qualifies the lead, and books them directly into your calendar. By the time you start your day, those enquiries are already confirmed appointments.',
  },
  {
    q: 'Which plan is right for my business?',
    a: 'We offer four tiers depending on where you are and where you want to go. Starter (£149/mo) is perfect for businesses that want to stop losing leads. Pro (£299/mo) adds full booking automation and follow-up sequences. Our most popular plan. Scale (£599/mo) gives you a fully automated business operating system with CRM and multi-channel comms. Custom (from £799/mo) is built entirely around your specific needs. We will recommend the right fit on your onboarding call.',
  },
  {
    q: 'Which channels does the AI work across?',
    a: 'One system covers all of them: WhatsApp, Instagram DMs, Facebook Messenger, email, SMS, and your website chat. Every channel feeds into one unified inbox and automation flow. No lead gets missed regardless of where they reach out.',
  },
  {
    q: 'What about no-shows and cancellations?',
    a: 'The AI sends automated reminders before every appointment, dramatically reducing no-show rates. For cancellations, it manages rescheduling, updates your calendar, and can offer the freed slot to clients on a waiting list. Revenue that used to get lost now gets recovered automatically.',
  },
  {
    q: 'How long does setup take and do I need technical knowledge?',
    a: 'Setup typically takes 30 to 60 days depending on the type and complexity of your automation. Simpler systems like Starter go live closer to 30 days. More complex builds like Scale or Custom can take up to 60 days to configure, test, and fully deploy. We handle everything. Zero technical knowledge required from you.',
  },
  {
    q: 'Is there a long-term contract?',
    a: 'No. All plans are month-to-month. Cancel anytime with 30 days notice. We do not lock anyone in because we do not need to. Our results speak for themselves and we would rather earn your business every single month.',
  },
];

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-24 bg-ea-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: heading */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
                <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Common Questions</span>
              </div>
              <h2 className="text-4xl font-bold text-white mb-5 leading-tight">
                Questions From{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                  Businesses Like Yours
                </span>
              </h2>
              <p className="text-ea-gray text-base leading-relaxed mb-8">
                Straight answers to the things appointment-based businesses actually want to know before getting started.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-ea-blue hover:bg-ea-blue-light text-white font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                style={{ boxShadow: '0 0 20px rgba(14, 165, 233, 0.25)' }}
              >
                Book a Free Demo <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          {/* Right: accordion */}
          <div className="lg:col-span-8 space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`rounded-xl border transition-all duration-300 overflow-hidden ${
                  openIndex === idx
                    ? 'border-ea-blue/50 bg-ea-dark-card'
                    : 'border-ea-dark-border bg-ea-dark-card hover:border-ea-blue/30'
                }`}
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                  onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                >
                  <span className={`font-medium text-sm pr-6 leading-relaxed transition-colors duration-200 ${openIndex === idx ? 'text-white' : 'text-white/80'}`}>
                    {faq.q}
                  </span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full border flex-shrink-0 transition-all duration-300 ${
                    openIndex === idx ? 'bg-ea-blue border-ea-blue' : 'border-ea-dark-border'
                  }`}>
                    <i className={`text-sm transition-transform duration-300 ${openIndex === idx ? 'ri-subtract-line text-white' : 'ri-add-line text-ea-gray'}`}></i>
                  </div>
                </button>
                <div
                  className={`transition-all duration-300 overflow-hidden ${openIndex === idx ? 'max-h-64' : 'max-h-0'}`}
                >
                  <div className="px-6 pb-5">
                    <div className="h-px bg-ea-dark-border mb-4"></div>
                    <p className="text-ea-gray text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
